/**
 * BaseModal - Componente base reutilizable para modales
 * Todos los modales de la aplicación deben usar este componente
 * para mantener consistencia visual.
 */

import React from 'react';
import trimergyLogo from '../../assets/images/logos/Trimergi_simbolo_color.svg';
import './BaseModal.css';

// Icono de cierre estilo lucide (trazo 2px, viewBox 24, currentColor)
const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const BaseModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'medium', // 'small' | 'medium' | 'large' | 'fullscreen'
  showLogo = true,
  className = '',
  embedded = false // render inline (como sección) en vez de overlay modal
}) => {
  // Modo embebido: el contenido vive dentro de un layout (p. ej. OptrimArea),
  // sin overlay ni cierre. La visibilidad la controla el contenedor.
  if (embedded) {
    return (
      <div className={`trimergy-modal-embedded ${className}`}>
        {title && <h3 className="trimergy-modal-embedded-title">{title}</h3>}
        <div className="trimergy-modal-embedded-body">{children}</div>
        {footer && <div className="trimergy-modal-embedded-footer">{footer}</div>}
      </div>
    );
  }

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizeClass = {
    small: 'trimergy-modal--small',
    medium: 'trimergy-modal--medium',
    large: 'trimergy-modal--large',
    fullscreen: 'trimergy-modal--fullscreen'
  }[size] || '';

  return (
    <div className="trimergy-modal-overlay" onClick={handleOverlayClick}>
      <div 
        className={`trimergy-modal ${sizeClass} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="trimergy-modal-header">
          <div className="trimergy-modal-header-content">
            {showLogo && (
              <img 
                src={trimergyLogo} 
                alt="Trimergy" 
                className="trimergy-modal-logo" 
              />
            )}
            <h2 className="trimergy-modal-title">{title}</h2>
          </div>
          <button 
            className="trimergy-modal-close" 
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="trimergy-modal-body">
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div className="trimergy-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// Componentes auxiliares para usar dentro del modal
export const ModalText = ({ children, center = false, className = '' }) => (
  <p className={`trimergy-modal-text ${center ? 'trimergy-modal-text--center' : ''} ${className}`}>
    {children}
  </p>
);

export const ModalInfoBox = ({ 
  title, 
  children, 
  variant = 'success' // 'success' | 'warning' | 'danger' | 'info'
}) => (
  <div className={`trimergy-info-box ${variant !== 'success' ? `trimergy-info-box--${variant}` : ''}`}>
    {title && <p className="trimergy-info-box-title">{title}</p>}
    {children}
  </div>
);

export const ModalButton = ({ 
  children, 
  onClick, 
  variant = 'ghost', // 'ghost' | 'primary' | 'success' | 'danger' | 'filled-primary' | 'filled-success' | 'filled-danger'
  disabled = false,
  loading = false,
  className = '',
  type = 'button'
}) => (
  <button
    type={type}
    className={`trimergy-btn trimergy-btn--${variant} ${className}`}
    onClick={onClick}
    disabled={disabled || loading}
  >
    {loading && <span className="trimergy-spinner trimergy-spinner--small" />}
    {children}
  </button>
);

export const ModalSpinner = ({ variant = 'primary', small = false, large = false }) => (
  <span className={`trimergy-spinner ${variant === 'success' ? 'trimergy-spinner--success' : ''} ${small ? 'trimergy-spinner--small' : ''} ${large ? 'trimergy-spinner--large' : ''}`} />
);

export default BaseModal;
